import { getErrorMessage, getSocketHost } from '../utils/index.js';
import { isAuthError } from './AuthError.js';
import RequestClient, { authErrorStatusCode, } from './RequestClient.js';
const getName = (id) => {
    return id
        .split('-')
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(' ');
};
function getOrigin() {
    return location.origin;
}
export default class Provider extends RequestClient {
    #refreshingTokenPromise;
    provider;
    id;
    name;
    pluginId;
    tokenKey;
    companionKeysParams;
    preAuthToken;
    supportsRefreshToken;
    constructor(uppy, opts) {
        super(uppy, opts);
        this.provider = opts.provider;
        this.id = this.provider;
        this.name = opts.name || getName(this.id);
        this.pluginId = opts.pluginId;
        this.tokenKey = `companion-${this.pluginId}-auth-token`;
        this.companionKeysParams = this.opts.companionKeysParams;
        this.preAuthToken = null;
        this.supportsRefreshToken = !!opts.supportsRefreshToken;
    }
    async headers() {
        const [headers, token] = await Promise.all([
            super.headers(),
            this.#getAuthToken(),
        ]);
        const authHeaders = {};
        if (token) {
            authHeaders['uppy-auth-token'] = token;
        }
        if (this.companionKeysParams) {
            authHeaders['uppy-credentials-params'] = btoa(JSON.stringify({ params: this.companionKeysParams }));
        }
        return { ...headers, ...authHeaders };
    }
    onReceiveResponse(response) {
        super.onReceiveResponse(response);
        const plugin = this.#getPlugin();
        const oldAuthenticated = plugin.getPluginState().authenticated;
        const authenticated = oldAuthenticated
            ? response.status !== authErrorStatusCode
            : response.status < 400;
        plugin.setPluginState({ authenticated });
        return response;
    }
    async setAuthToken(token) {
        await this.#getPlugin().storage.setItem(this.tokenKey, token);
    }
    #getAuthToken() {
        return this.#getPlugin().storage.getItem(this.tokenKey);
    }
    async removeAuthToken() {
        await this.#getPlugin().storage.removeItem(this.tokenKey);
    }
    #getPlugin() {
        const plugin = this.uppy.getPlugin(this.pluginId);
        if (plugin == null)
            throw new Error('Plugin was nullish');
        return plugin;
    }
    /**
     * Ensure we have a preauth token if necessary. Attempts to fetch one if we don't,
     * or rejects if loading one fails.
     */
    async ensurePreAuth() {
        if (this.companionKeysParams && !this.preAuthToken) {
            await this.fetchPreAuthToken();
            if (!this.preAuthToken) {
                throw new Error('Could not load authentication data required for third-party login. Please try again later.');
            }
        }
    }
    authQuery(_data) {
        return {};
    }
    authUrl({ authFormData, query, authCallbackToken, }) {
        const searchParams = new URLSearchParams({
            ...query,
            // `origin` is only used for Companion instances configured to accept multiple origins.
            state: btoa(JSON.stringify({ origin: getOrigin() })),
            ...this.authQuery({ authFormData }),
        });
        if (this.preAuthToken) {
            searchParams.set('uppyPreAuthToken', this.preAuthToken);
        }
        if (authCallbackToken) {
            searchParams.set('authCallbackToken', authCallbackToken);
        }
        return `${this.hostname}/${this.id}/connect?${searchParams}`;
    }
    async loginSimpleAuth({ uppyVersions, authFormData, signal, }) {
        const response = await this.post(`${this.id}/simple-auth`, { form: authFormData }, { qs: { uppyVersions }, signal });
        signal.throwIfAborted();
        await this.setAuthToken(response.uppyAuthToken);
        signal.throwIfAborted();
    }
    async loginOAuth({ uppyVersions, authFormData, signal, }) {
        await this.ensurePreAuth();
        signal.throwIfAborted();
        // Important: We need to do this synchronously, or else browsers might block the popup.
        // (We cannot wait until the websocket connection is established).
        // This opens up a race condtition if the websocket is not connected before the user
        // completes(or cancels) authentication, but that’s a small compromose we gotta make.
        const authCallbackToken = crypto.randomUUID();
        const link = this.authUrl({
            query: { uppyVersions },
            authFormData,
            authCallbackToken,
        });
        const authWindow = window.open(link, '_blank');
        let interval;
        let webSocket;
        try {
            const host = getSocketHost(this.opts.companionUrl);
            // Note that this promise is not guaranteed to settle in all cases
            const token = await new Promise((resolve, reject) => {
                webSocket = new WebSocket(`${host}/api2/auth-callback/token/${authCallbackToken}`);
                webSocket.addEventListener('close', () => {
                    reject(new Error('Socket closed'));
                });
                webSocket.addEventListener('error', (error) => {
                    this.uppy.log(`Companion socket error ${JSON.stringify(error)}, closing socket`, 'warning');
                    webSocket?.close(); // 'close' event will be emitted
                });
                webSocket.addEventListener('message', (e) => {
                    try {
                        const { token, error } = JSON.parse(e.data);
                        if (error) {
                            reject(new Error('Authentication reported error'));
                        }
                        else if (!token) {
                            reject(new Error('Authentication did not return a token'));
                        }
                        else {
                            resolve(token);
                        }
                    }
                    catch (err) {
                        reject(err);
                    }
                });
                signal.addEventListener('abort', () => reject(new Error('Authentication was aborted')));
                // poll for user closure of the window, so we can reject when it happens
                if (authWindow) {
                    interval = window.setInterval(() => {
                        if (authWindow.closed) {
                            reject(new Error('Auth window was closed by the user'));
                        }
                    }, 500);
                }
            });
            this.setAuthToken(token);
        }
        catch (err) {
            const message = this.uppy.i18n('authAborted');
            this.uppy.info({ message }, 'warning', 5000);
            this.uppy.log(`Authentication failed: ${getErrorMessage(err)}`, 'warning');
            throw err;
        }
        finally {
            // cleanup:
            // if we don't setTimeout, the window doesn't really close (I don't know why).
            setTimeout(() => authWindow?.close(), 1);
            this.uppy.log(`Closing auth callback socket ${authCallbackToken}`);
            webSocket?.close();
            clearInterval(interval);
        }
    }
    login({ uppyVersions = '', authFormData, signal, }) {
        return this.loginOAuth({ uppyVersions, authFormData, signal });
    }
    refreshTokenUrl() {
        return `${this.hostname}/${this.id}/refresh-token`;
    }
    fileUrl(id) {
        return `${this.hostname}/${this.id}/get/${id}`;
    }
    async request(...args) {
        await this.#refreshingTokenPromise;
        try {
            // to test simulate access token expired (leading to a token token refresh),
            // see mockAccessTokenExpiredError in companion/drive.
            // If you want to test refresh token *and* access token invalid, do this for example with Google Drive:
            // While uploading, go to your google account settings,
            // "Third-party apps & services", then click "Companion" and "Remove access".
            return await super.request(...args);
        }
        catch (err) {
            if (!this.supportsRefreshToken)
                throw err;
            // only handle auth errors (401 from provider), and only handle them if we have a (refresh) token
            const authTokenAfter = await this.#getAuthToken();
            if (!isAuthError(err) || !authTokenAfter)
                throw err;
            if (this.#refreshingTokenPromise == null) {
                // Many provider requests may be starting at once, however refresh token should only be called once.
                // Once a refresh token operation has started, we need all other request to wait for this operation (atomically)
                this.#refreshingTokenPromise = (async () => {
                    try {
                        this.uppy.log(`[CompanionClient] Refreshing expired auth token`);
                        const response = await super.request({
                            path: this.refreshTokenUrl(),
                            method: 'POST',
                        });
                        await this.setAuthToken(response.uppyAuthToken);
                    }
                    catch (refreshTokenErr) {
                        if (isAuthError(refreshTokenErr)) {
                            // if refresh-token has failed with auth error, delete token, so we don't keep trying to refresh in future
                            await this.removeAuthToken();
                        }
                        throw err;
                    }
                    finally {
                        this.#refreshingTokenPromise = undefined;
                    }
                })();
            }
            await this.#refreshingTokenPromise;
            // now retry the request with our new refresh token
            return super.request(...args);
        }
    }
    async fetchPreAuthToken() {
        if (!this.companionKeysParams) {
            return;
        }
        try {
            // Preauth configures app credentials, not the user's provider session.
            // Bypass provider headers, auth state, and refresh, but keep host discovery.
            const res = await super.request({
                path: `${this.id}/preauth/`,
                method: 'POST',
                data: { params: this.companionKeysParams },
                skipSubclassHooks: true,
            });
            this.preAuthToken = res.token;
        }
        catch (err) {
            this.uppy.log(`[CompanionClient] unable to fetch preAuthToken ${err}`, 'warning');
        }
    }
    list(directory, options) {
        return this.get(`${this.id}/list/${directory || ''}`, options);
    }
    search(text, options = {}) {
        const qs = new URLSearchParams();
        qs.set('q', text);
        if (options.path)
            qs.set('path', options.path);
        if (options.cursor)
            qs.set('cursor', options.cursor);
        const base = `${this.id}/search`;
        const path = `${base}?${qs.toString()}`;
        return this.get(path, options);
    }
    async logout(options) {
        const response = await this.get(`${this.id}/logout`, options);
        await this.removeAuthToken();
        return response;
    }
    /**
     * @experimental Part of the file-management API added for `@uppy/s3`: it
     * will change incompatibly, also in minor releases.
     */
    deleteItem(id, options) {
        return this.post(`${this.id}/mutate/delete`, { id }, options);
    }
    /**
     * @experimental Part of the file-management API added for `@uppy/s3`: it
     * will change incompatibly, also in minor releases.
     */
    moveItem(id, destination, options) {
        return this.post(`${this.id}/mutate/move`, { id, destination }, options);
    }
    /**
     * @experimental Part of the file-management API added for `@uppy/s3`: it
     * will change incompatibly, also in minor releases.
     */
    createFolder(parentId, name, options) {
        return this.post(`${this.id}/mutate/create-folder`, { parentId, name }, options);
    }
}
